import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import MangaCard from "@/components/feature/MangaCard";
import { mangaWorks } from "@/mocks/manga";

const chapterTitles = [
  "初雪之约", "雪夜来信", "山径的灯", "沉默的邮差", "融化的心事",
  "北风与炉火", "未寄出的信", "小镇的春天", "再次遇见", "雪落无声",
];

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = mangaWorks.find((w) => w.id === id);

  const [fav, setFav] = useState(false);
  const [activeChapter, setActiveChapter] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const chapters = useMemo(
    () =>
      work
        ? Array.from({ length: work.episodes }, (_, i) => ({
            no: i + 1,
            title: chapterTitles[i % chapterTitles.length],
          }))
        : [],
    [work]
  );

  const related = useMemo(
    () => (work ? mangaWorks.filter((w) => w.categorySlug === work.categorySlug && w.id !== work.id) : []),
    [work]
  );

  if (!work) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center px-4 pt-24 text-center">
          <span className="text-5xl text-foreground-500"><i className="ri-emotion-sad-line"></i></span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">作品不存在或已下架</h1>
          <Link to="/" className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 hover:bg-primary-600">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const startReading = () => {
    document.getElementById("chapters")?.scrollIntoView({ behavior: "smooth" });
  };

  const openChapter = (no: number, title: string) => {
    setActiveChapter(no);
    setToast(`已开始阅读：第 ${no} 话 · ${title}`);
    window.setTimeout(() => setToast(null), 2600);
  };

  return (
    <Layout>
      <Seo
        title={`${work.title} ${work.categoryName}在线阅读 - 吹雪漫画网`}
        description={`${work.title}：${work.intro} ${work.categoryName}在线看，高清原图、分章阅读，追更不迷路。`}
        keywords="吹雪漫画网,雪景漫画,冬日漫画,雪国漫画,冬物语,冰雪漫画,雪女漫画"
        canonical={`https://secretom.github.io/work/${work.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Book",
            name: work.title,
            author: { "@type": "Person", name: work.author },
            illustrator: { "@type": "Person", name: work.artist },
            genre: work.categoryName,
            numberOfPages: work.episodes,
            rating: { "@type": "Rating", ratingValue: work.rating, bestRating: 10 },
            description: work.longIntro,
            url: `https://secretom.github.io/work/${work.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://secretom.github.io/" },
              { "@type": "ListItem", position: 2, name: work.categoryName, item: `https://secretom.github.io/category/${work.categorySlug}` },
              { "@type": "ListItem", position: 3, name: work.title },
            ],
          },
        ]}
      />

      {/* 头部信息 */}
      <section className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
          <nav aria-label="面包屑" className="mb-7 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
            <Link to="/" className="hover:text-primary-500">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${work.categorySlug}`} className="hover:text-primary-500">{work.categoryName}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400">{work.title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-8 pb-10 md:pb-14 lg:grid-cols-[320px_1fr] lg:items-start">
            {/* 封面 */}
            <div className="relative mx-auto w-full max-w-[280px] overflow-hidden rounded-xl border border-background-200/70 lg:max-w-none">
              <div className="aspect-[3/4]">
                <img src={work.cover} alt={`${work.title} ${work.categoryName}封面`} className="h-full w-full object-cover object-top" />
              </div>
              <span className="absolute right-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-950/70 px-3 py-1 text-sm text-accent-500 backdrop-blur-sm">
                <i className="ri-star-fill"></i> {work.rating.toFixed(1)}
              </span>
            </div>

            {/* 信息 */}
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {work.tags.map((t) => (
                  <span key={t} className="whitespace-nowrap rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-foreground-700">{t}</span>
                ))}
                <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs ${work.status === "连载中" ? "bg-accent-500 text-background-950" : "bg-secondary-500 text-background-950"}`}>
                  {work.status}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">{work.title}</h1>
              <p className="mt-2 text-sm text-foreground-500">
                {work.categoryName} · 作者 {work.author} · 绘者 {work.artist}
              </p>

              <p className="mt-5 max-w-2xl text-base leading-7 text-foreground-700">{work.intro}</p>

              <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { k: "状态", v: work.status },
                  { k: "章节", v: `${work.episodes} 话` },
                  { k: "人气", v: work.views },
                  { k: "更新", v: work.latestChapter.split("·")[0] },
                ].map((it) => (
                  <div key={it.k} className="rounded-lg border border-background-200/60 bg-background-100 px-4 py-3">
                    <dt className="text-xs text-foreground-500">{it.k}</dt>
                    <dd className="mt-1 font-heading text-base font-semibold text-foreground-950">{it.v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={startReading}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line"></i> 开始阅读
                </button>
                <button
                  type="button"
                  onClick={() => setFav((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-7 py-3 text-sm font-semibold transition-colors ${
                    fav
                      ? "border-primary-500 bg-primary-500/10 text-primary-500"
                      : "border-background-300/70 text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {fav ? "已收藏" : "加入收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 简介 + 章节 */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          {/* 作品简介 */}
          <article className="lg:col-span-1">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-500"></i> 作品简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">{work.longIntro}</p>
            <div className="mt-6 rounded-lg border border-background-200/60 bg-background-100 p-4 text-sm text-foreground-600">
              <p className="flex items-center justify-between py-1"><span className="text-foreground-500">作者</span><span className="text-foreground-950">{work.author}</span></p>
              <p className="flex items-center justify-between py-1"><span className="text-foreground-500">绘者</span><span className="text-foreground-950">{work.artist}</span></p>
              <p className="flex items-center justify-between py-1"><span className="text-foreground-500">题材</span><span className="text-foreground-950">{work.categoryName}</span></p>
              <p className="flex items-center justify-between py-1"><span className="text-foreground-500">人气</span><span className="text-foreground-950">{work.views}</span></p>
            </div>
          </article>

          {/* 分章阅读 */}
          <div id="chapters" className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-file-list-3-line text-primary-500"></i> 分章阅读（共 {work.episodes} 话）
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {chapters.map((c) => (
                <button
                  key={c.no}
                  type="button"
                  onClick={() => openChapter(c.no, c.title)}
                  className={`flex flex-col items-start rounded-lg border px-4 py-3 text-left transition-colors ${
                    activeChapter === c.no
                      ? "border-primary-500 bg-primary-500/10"
                      : "border-background-200/60 bg-background-100 hover:border-primary-500/40"
                  }`}
                >
                  <span className="font-heading text-sm font-semibold text-foreground-950">第 {c.no} 话</span>
                  <span className="mt-0.5 text-xs text-foreground-500">{c.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-7 flex items-end justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-sparkling-line text-accent-500"></i> 同题材推荐
              </h2>
              <Link to={`/category/${work.categorySlug}`} className="text-sm text-foreground-600 hover:text-primary-500">
                更多{work.categoryName} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((w) => (
                <MangaCard key={w.id} work={w} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 阅读反馈 toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <p className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-950/90 px-5 py-3 text-sm text-foreground-950 shadow-soft">
            <i className="ri-bookmark-fill text-primary-500"></i> {toast}
          </p>
        </div>
      )}
    </Layout>
  );
}